import React, { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LuSettings2, LuRectangleVertical, LuRectangleHorizontal } from 'react-icons/lu';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import '../../styles/PrintableLayout.css';

/* ── Constants ───────────────────────────────────────────────────── */
const SIZE_LABELS = {
  '5r':  '5R  (5" × 7")',
  'a5':  'A5  (5.8" × 8.3")',
  '4x6': '4×6"',
  '5x5': '5×5" Square',
};
const ORIENT_ICONS = {
  portrait:  <><LuRectangleVertical />   Portrait</>,
  landscape: <><LuRectangleHorizontal /> Landscape</>,
};

// Page dimensions in mm [width, height] for portrait; landscape swaps them
const PAGE_DIMS = {
  '5r':  [127, 178],
  'a5':  [148, 210],
  '4x6': [102, 152],
  '5x5': [127, 127],
};
const SIZE_SHORT = { '5r': '5R', 'a5': 'A5', '4x6': '4x6', '5x5': '5x5' };

// 1 mm in px at 96 dpi
const MM_TO_PX = 3.7795;
// .pl-pages padding (top+bottom) + per-gap between pages
const PAGES_PAD = 48;
const PAGE_GAP  = 24;

/* ════════════════════════════════════════════════════════════════════
   PRINTABLE LAYOUT
   Generic shell for any printable invitation.
   Props:
     title    — shown in the sticky topbar
     filename — PDF base name (e.g. "jenine-charisse-invitation")
     children — the <article> page elements
   ════════════════════════════════════════════════════════════════════ */
export default function PrintableLayout({ title, filename, children }) {
  const [size, setSize] = useState('5r');            // '5r' | 'a5' | '4x6' | '5x5'
  const [orient, setOrient] = useState('landscape'); // 'portrait' | 'landscape'
  const [saving, setSaving] = useState(false);
  const [savedFilename, setSavedFilename] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);

  // Recompute preview scale whenever size, orient, or viewport changes.
  // Only affects the on-screen transform — html2canvas reads .pl-page
  // elements directly and is completely unaffected by this.
  useEffect(() => {
    const update = () => {
      const [pw, ph] = PAGE_DIMS[size];
      const [pageW] = orient === 'landscape' && pw !== ph ? [ph, pw] : [pw, ph];
      const pageWpx  = pageW * MM_TO_PX;
      const available = window.innerWidth - 32; // 16px padding each side
      setPreviewScale(available < pageWpx ? Math.max(0.35, available / pageWpx) : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [size, orient]);

  const handleSavePDF = async () => {
    setSaving(true);

    // Strip the mobile preview transform so html2canvas captures pages at
    // their true layout size. Parent transform shifts elements relative to
    // the viewport, which html2canvas uses when compositing — leaving it on
    // produces broken/overlapping output on narrow screens.
    const pagesEl = document.querySelector('.pl-pages');
    const savedTransform    = pagesEl?.style.transform    ?? '';
    const savedMarginBottom = pagesEl?.style.marginBottom ?? '';
    if (pagesEl) {
      pagesEl.style.transform    = 'none';
      pagesEl.style.marginBottom = '';
    }

    try {
      const [pw, ph] = PAGE_DIMS[size];
      const [w, h] = orient === 'landscape' && pw !== ph ? [ph, pw] : [pw, ph];

      const pdf = new jsPDF({
        orientation: orient === 'landscape' ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [w, h],
      });

      const pages = document.querySelectorAll('.pl-page');
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], {
          scale: 3,
          useCORS: true,
          backgroundColor: null,
          logging: false,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        if (i > 0) pdf.addPage([w, h], orient === 'landscape' ? 'landscape' : 'portrait');
        pdf.addImage(imgData, 'JPEG', 0, 0, w, h);
      }

      pdf.save(`${filename}-${SIZE_SHORT[size]}-${orient}.pdf`);
      setSavedFilename(`${filename}-${SIZE_SHORT[size]}-${orient}.pdf`);
    } finally {
      // Restore preview transform
      if (pagesEl) {
        pagesEl.style.transform    = savedTransform;
        pagesEl.style.marginBottom = savedMarginBottom;
      }
      setSaving(false);
    }
  };

  return (
    <div className={`pl-wrapper pl-size-${size} pl-orient-${orient}`}>

      {/* ── Generating PDF overlay (non-closable) ────────────────── */}
      {saving && (
        <div className="pl-generating-overlay" role="dialog" aria-modal="true" aria-label="Generating PDF">
          <div className="pl-generating-box">
            <AiOutlineLoading3Quarters className="pl-generating-spinner" />
            <p className="pl-generating-title">Generating PDF…</p>
            <p className="pl-generating-sub">Please wait, this may take a moment.</p>
          </div>
        </div>
      )}

      {/* ── PDF saved confirmation modal ────────────────────────── */}
      {savedFilename && (
        <div className="pl-generating-overlay" role="dialog" aria-modal="true" aria-label="PDF saved">
          <div className="pl-generating-box">
            <div className="pl-done-icon">✓</div>
            <p className="pl-generating-title">PDF Saved!</p>
            <p className="pl-generating-sub pl-done-filename">{savedFilename}</p>
            <p className="pl-generating-sub">Your printable invitation has been downloaded.</p>
            <button className="pl-done-btn" onClick={() => setSavedFilename('')}>Done</button>
          </div>
        </div>
      )}

      {/* ── Options modal ─────────────────────────────────────────── */}
      {showOptions && (
        <div className="pl-modal-backdrop" onClick={() => setShowOptions(false)}>
          <div className="pl-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Print options">
            <div className="pl-modal-header">
              <h2 className="pl-modal-title">Print Options</h2>
              <button className="pl-modal-close" onClick={() => setShowOptions(false)} aria-label="Close">×</button>
            </div>

            <div className="pl-modal-section">
              <p className="pl-modal-label">Paper Size</p>
              <div className="pl-opt-group">
                {Object.entries(SIZE_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`pl-opt-btn${size === key ? ' pl-opt-btn--active' : ''}`}
                    onClick={() => setSize(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pl-modal-section">
              <p className="pl-modal-label">Orientation</p>
              <div className="pl-opt-group">
                {Object.entries(ORIENT_ICONS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`pl-opt-btn${orient === key ? ' pl-opt-btn--active' : ''}`}
                    onClick={() => setOrient(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pl-modal-footer">
              <button className="pl-modal-done" onClick={() => setShowOptions(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticky screen toolbar ──────────────────────────────────── */}
      <div className="pl-topbar">
        <h1 className="pl-topbar-title">{title}</h1>
        <div className="pl-topbar-actions">
          <button
            type="button"
            className="pl-btn-options"
            onClick={() => setShowOptions(true)}
            disabled={saving}
          >
            <LuSettings2 /> Options
          </button>
          <button
            type="button"
            className="pl-btn-print"
            onClick={handleSavePDF}
            disabled={saving}
          >
          <HiOutlineDocumentArrowDown /> Save as PDF
          </button>
        </div>
      </div>

      {/* ── All pages ─────────────────────────────────────────────────
           previewScale shrinks pages on narrow viewports for readability.
           html2canvas queries .pl-page directly — transform on the wrapper
           has zero effect on the PDF output.
         ─────────────────────────────────────────────────────────────── */}
      {(() => {
        const pageCount = React.Children.count(children);
        const [pw, ph]  = PAGE_DIMS[size];
        const [, pageH] = orient === 'landscape' && pw !== ph ? [ph, pw] : [pw, ph];
        const naturalH  = pageCount * (pageH * MM_TO_PX) + (pageCount - 1) * PAGE_GAP + PAGES_PAD;
        const pagesStyle = previewScale < 1 ? {
          transform:      `scale(${previewScale})`,
          transformOrigin: 'top center',
          marginBottom:   `${naturalH * (previewScale - 1)}px`,
        } : undefined;
        return (
          <div className="pl-pages" style={pagesStyle}>
            {React.Children.map(children, (child, i) =>
              React.cloneElement(
                child,
                {},
                ...(Array.isArray(child.props.children) ? child.props.children : [child.props.children]),
                <span key="pagenum" className="pl-page-num" aria-hidden="true">{i + 1}</span>
              )
            )}
          </div>
        );
      })()}
    </div>
  );
}
